import type { TFunction } from "i18next";
import { getStoredCustomerRouteId } from "@/context/customer/customerRouteId";
import {
  RecentlyViewedEntityType,
  type RecentlyViewedItem as RecentlyViewedItemData,
  type RecordRecentlyViewedItemInput,
} from "@/gql/graphql";
import type {
  RecentlyViewedItem,
  RecentlyViewedItemType,
} from "@/components/app/Navigation/GlobalHeader/RecentlyViewed/types";
import { isGqlType } from "@/utils/typeGuards";

const entityTypeToUiType: Record<
  RecentlyViewedEntityType,
  RecentlyViewedItemType
> = {
  [RecentlyViewedEntityType.Contract]: "contract",
  [RecentlyViewedEntityType.Amendment]: "amendment",
  [RecentlyViewedEntityType.Customer]: "customer",
  [RecentlyViewedEntityType.Device]: "device",
};

export function getRecentlyViewedItemId(
  item: Pick<
    RecentlyViewedItemData,
    "entityType" | "contractId" | "quoteId" | "customerId" | "iamDeviceId"
  >
): string {
  switch (item.entityType) {
    case RecentlyViewedEntityType.Contract:
      return `contract:${item.contractId ?? ""}`;
    case RecentlyViewedEntityType.Amendment:
      return `amendment:${item.quoteId ?? ""}`;
    case RecentlyViewedEntityType.Customer:
      return `customer:${item.customerId ?? ""}`;
    case RecentlyViewedEntityType.Device:
      return `device:${item.iamDeviceId ?? ""}`;
  }
}

export function recentlyViewedTitle(
  item: Pick<
    RecentlyViewedItemData,
    | "entityType"
    | "contractNumber"
    | "purchaseOrderNumber"
    | "quoteId"
    | "customerName"
    | "model"
    | "serialNumber"
  >,
  t: TFunction
): string {
  switch (item.entityType) {
    case RecentlyViewedEntityType.Contract: {
      const contractNumber = item.contractNumber ?? "";
      if (item.purchaseOrderNumber) {
        return t("global_settings.recently_viewed.contract_with_po", {
          contractNumber,
          purchaseOrderNumber: item.purchaseOrderNumber,
        });
      }
      return t("global_settings.recently_viewed.contract", { contractNumber });
    }
    case RecentlyViewedEntityType.Amendment: {
      const quoteId = item.quoteId ?? "";
      if (item.purchaseOrderNumber) {
        return t("global_settings.recently_viewed.amendment_with_po", {
          quoteId,
          purchaseOrderNumber: item.purchaseOrderNumber,
        });
      }
      return t("global_settings.recently_viewed.amendment", { quoteId });
    }
    case RecentlyViewedEntityType.Customer:
      return item.customerName ?? "";
    case RecentlyViewedEntityType.Device: {
      const serialNumber = item.serialNumber ?? "";
      if (item.model) {
        return t("global_settings.recently_viewed.device_with_model", {
          modelName: item.model,
          serialNumber,
        });
      }
      return t("global_settings.recently_viewed.device", { serialNumber });
    }
  }
}

export function recentlyViewedSubtitle(
  item: Pick<RecentlyViewedItemData, "entityType" | "customerName">
): string {
  if (item.entityType === RecentlyViewedEntityType.Customer) {
    return "";
  }
  return item.customerName ?? "";
}

export function recentlyViewedPath(
  accountId: string,
  item: Pick<
    RecentlyViewedItemData,
    "entityType" | "contractId" | "quoteId" | "customerId"
  >
): string {
  switch (item.entityType) {
    case RecentlyViewedEntityType.Contract:
      return `/${accountId}/contracts/${item.contractId ?? ""}`;
    case RecentlyViewedEntityType.Amendment:
      return `/${accountId}/contracts/${item.contractId ?? ""}/amendments/${item.quoteId ?? ""}`;
    case RecentlyViewedEntityType.Customer:
      return `/${accountId}/customers/${getStoredCustomerRouteId(item.customerId ?? "")}`;
    case RecentlyViewedEntityType.Device:
      return `/${accountId}/devices`;
  }
}

export function mapRecentlyViewedItem(
  accountId: string,
  item: RecentlyViewedItemData,
  t: TFunction
): RecentlyViewedItem {
  return {
    id: getRecentlyViewedItemId(item),
    type: entityTypeToUiType[item.entityType],
    title: recentlyViewedTitle(item, t),
    subtitle: recentlyViewedSubtitle(item),
    visitedAt: item.viewedAt,
    path: recentlyViewedPath(accountId, item),
    serialNumber: item.serialNumber ?? undefined,
  };
}

export type RecordRecentlyViewedVisitInput = Omit<
  RecordRecentlyViewedItemInput,
  "dealerAccountId"
> & {
  dealerAccountId: string;
};

type DeviceOwnerInput = {
  ownerName?: string | null;
  desiredDealerCustomer?: { companyName?: string | null } | null;
  owner?:
    | { __typename?: "Account"; companyName?: string | null }
    | { __typename?: "DealerCustomer"; companyName?: string | null }
    | { __typename?: "AnonymisedOwner"; companyName?: string | null }
    | null;
};

export function getDeviceCustomerName(
  device: DeviceOwnerInput
): string | undefined {
  if (device.owner != null && isGqlType(device.owner, "DealerCustomer")) {
    return device.owner.companyName ?? undefined;
  }
  if (device.ownerName) {
    return device.ownerName;
  }

  if (device.desiredDealerCustomer?.companyName) {
    return device.desiredDealerCustomer.companyName;
  }

  if (device.owner != null && isGqlType(device.owner, "Account")) {
    return device.owner.companyName ?? undefined;
  }

  return undefined;
}
